import React, { useState, useRef, useEffect } from 'react';
import { Phone, MessageCircle, Bot, X, Send } from 'lucide-react';
import './ContactWidgets.css';

const PHONE_NUMBER = '+966501234567';
const WHATSAPP_NUMBER = '966501234567';

const AI_RESPONSES = {
  greeting: "Assalamu Alaikum! Welcome to Bayt Al-Khayr. I'm your AI assistant. How can I help you today?",
  shipping: "We offer FREE shipping on orders over 500 SAR. Standard delivery takes 3-7 business days within Saudi Arabia, and 7-14 days internationally to 100+ countries.",
  products: "We offer premium ethically sourced products from Medina including Ajwa Dates, Prayer Gloves, Hijabs, Tasbih Beads, Oud Perfume, and more. Visit our Collection page to browse!",
  returns: "We have a 30-day easy return policy. Items must be unused and in original packaging. Contact us for a return label.",
  payment: "We accept Visa, Mastercard, Apple Pay, and Cash on Delivery. All payments are 100% secure.",
  halal: "All our products are Halal certified and ethically sourced from trusted suppliers in Medina.",
  contact: "You can reach us via WhatsApp, phone at +966 50 123 4567, or email at info@baytal-khayr.com. Our team is available 9 AM - 9 PM (AST).",
  default: "Thank you for your question! For detailed assistance, please contact us via WhatsApp or phone. You can also email info@baytal-khayr.com.",
};

function getAIResponse(message) {
  const msg = message.toLowerCase();
  if (msg.includes('ship') || msg.includes('deliver')) return AI_RESPONSES.shipping;
  if (msg.includes('product') || msg.includes('collection') || msg.includes('buy')) return AI_RESPONSES.products;
  if (msg.includes('return') || msg.includes('refund')) return AI_RESPONSES.returns;
  if (msg.includes('pay') || msg.includes('card')) return AI_RESPONSES.payment;
  if (msg.includes('halal') || msg.includes('ethical')) return AI_RESPONSES.halal;
  if (msg.includes('contact') || msg.includes('phone') || msg.includes('email')) return AI_RESPONSES.contact;
  if (msg.includes('hello') || msg.includes('hi') || msg.includes('salam')) return AI_RESPONSES.greeting;
  return AI_RESPONSES.default;
}

function ContactWidgets() {
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([
    { from: 'bot', text: AI_RESPONSES.greeting },
  ]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  const sendMessage = (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMsg = input.trim();
    setMessages(prev => [...prev, { from: 'user', text: userMsg }]);
    setInput('');
    setTyping(true);

    setTimeout(() => {
      setMessages(prev => [...prev, { from: 'bot', text: getAIResponse(userMsg) }]);
      setTyping(false);
    }, 800 + Math.random() * 600);
  };

  const quickQuestions = ['Shipping info?', 'Our products?', 'Return policy?', 'Contact us'];

  return (
    <>
      <div className="contact-widgets">
        <a href={`tel:${PHONE_NUMBER}`} className="widget-btn phone-btn" aria-label="Call us">
          <Phone size={22} />
          <span className="widget-tooltip">Call Us</span>
        </a>
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}?text=Assalamu%20Alaikum!%20I%20have%20a%20question%20about%20Bayt%20Al-Khayr.`}
          target="_blank"
          rel="noopener noreferrer"
          className="widget-btn whatsapp-btn"
          aria-label="WhatsApp"
        >
          <MessageCircle size={22} />
          <span className="widget-tooltip">WhatsApp</span>
        </a>
        <button
          className={`widget-btn ai-btn ${chatOpen ? 'active' : ''}`}
          onClick={() => setChatOpen(!chatOpen)}
          aria-label="AI Chat"
        >
          <Bot size={22} />
          <span className="widget-tooltip">AI Chat</span>
        </button>
      </div>

      {chatOpen && (
        <div className="ai-chat-panel">
          <div className="ai-chat-header">
            <div className="ai-chat-title">
              <Bot size={20} />
              <div>
                <span>Bayt Al-Khayr Assistant</span>
                <small>AI-powered • Online</small>
              </div>
            </div>
            <button className="ai-chat-close" onClick={() => setChatOpen(false)}>
              <X size={20} />
            </button>
          </div>

          <div className="ai-chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`chat-bubble ${msg.from}`}>
                {msg.text}
              </div>
            ))}
            {typing && (
              <div className="chat-bubble bot typing">
                <span></span><span></span><span></span>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="ai-quick-questions">
            {quickQuestions.map(q => (
              <button key={q} onClick={() => {
                setMessages(prev => [...prev, { from: 'user', text: q }]);
                setTyping(true);
                setTimeout(() => {
                  setMessages(prev => [...prev, { from: 'bot', text: getAIResponse(q) }]);
                  setTyping(false);
                }, 600);
              }}>{q}</button>
            ))}
          </div>

          <form className="ai-chat-input" onSubmit={sendMessage}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything..."
            />
            <button type="submit"><Send size={18} /></button>
          </form>
        </div>
      )}
    </>
  );
}

export default ContactWidgets;
